import { useRef } from "react";

import { tap } from "~/tools/impulse";
import type { GetImpulseFormOutput } from "~/tools/impulse-form";

import { DashboardSpec, type DashboardWidgetSpecInitConfig } from "../../common/dashboard-spec";
import { Api } from "../../common/openapi";
import { useUserProfile } from "../../common/use-user-profile";
import {
	type DashboardReadFullSchema,
	cloneDashboard,
	createDashboard,
	queryAllDashboards,
	useOptimisticUpdateDashboard,
} from "../../queries/dashboards";
import { useMutation, useQueryClient } from "../../queries/react-query";
import { useErrorMessage } from "../_notification-error";

import type { SetupDashboardFormState } from "./_setup-dashboard-drawer-state";

export interface UseSetupDashboardParams {
	onSuccessfulCreate: (dashboard: DashboardReadFullSchema) => void;
	onSuccessfulClone: (dashboard: DashboardReadFullSchema) => void;
	onSuccessfulEdit: (dashboardName: string) => void;
	onSuccessfulStar: VoidFunction;
	onFailedStar: (error: unknown) => void;
}

export interface UseSetupDashboardResult {
	createDashboard(
		widgets: ReadonlyArray<DashboardWidgetSpecInitConfig>,
		values: GetImpulseFormOutput<SetupDashboardFormState>,
	): Promise<void>;

	cloneDashboard(
		sourceId: number,
		schema: undefined | DashboardSpec,
		values: GetImpulseFormOutput<SetupDashboardFormState>,
	): Promise<void>;

	editDashboard(id: number, form: SetupDashboardFormState, values: GetImpulseFormOutput<SetupDashboardFormState>): void;
}

export const useSetupDashboard = ({
	onSuccessfulCreate,
	onSuccessfulClone,
	onSuccessfulEdit,
	onSuccessfulStar,
	onFailedStar,
}: UseSetupDashboardParams): UseSetupDashboardResult => {
	// indicates if it should suppress the onStar callback if the dashboard is edited
	// so only one notification is shown
	const shouldCallOnStar = useRef(false);
	const queryClient = useQueryClient();
	const { displayErrorMessage } = useErrorMessage();

	const { mutate: edit } = useOptimisticUpdateDashboard({
		onError: (error) => {
			displayErrorMessage({
				message: "Something went wrong while updating dashboard",
				error,
			});
		},

		onSuccess: (dashboard) => {
			onSuccessfulEdit(dashboard.name);
		},
	});

	const [, updateProfile] = useUserProfile({
		onError: (error) => {
			if (shouldCallOnStar.current) {
				shouldCallOnStar.current = false;
				onFailedStar(error);
			}
		},

		onSuccess: () => {
			if (shouldCallOnStar.current) {
				shouldCallOnStar.current = false;
				onSuccessfulStar();
			}
		},
	});

	const { mutateAsync: clone } = useMutation({
		...cloneDashboard,
		onSuccess: async (dashboard) => {
			// wait for the invalidation so user sees the updated list of dashboards
			await queryAllDashboards.invalidate(queryClient);

			onSuccessfulClone(dashboard);
		},

		onError: (error) => {
			displayErrorMessage({
				message: "Something went wrong while cloning dashboard",
				error,
			});
		},
	});

	const { mutateAsync: create } = useMutation({
		...createDashboard,

		onSuccess: (dashboard) => {
			// do not wait for the invalidation because it redirects to the new dashboard
			void queryAllDashboards.reset(queryClient);

			onSuccessfulCreate(dashboard);
		},

		onError: (error) => {
			displayErrorMessage({
				message: "Something went wrong while creating new dashboard",
				error,
			});
		},
	});

	return {
		createDashboard: async (widgets, { dashboard, options: { starred, publiclyAccessible } }) => {
			const { id } = await create({
				name: dashboard.name,
				description: dashboard.description,
				scope: publiclyAccessible ? Api.DashboardScope.PublicDashboard : Api.DashboardScope.PrivateDashboard,
				spec: DashboardSpec.fromPerses({}).addWidgets(widgets),
			});

			if (starred) {
				// do not wait for the optimistic profile update
				updateProfile((profile) => ({
					...profile,
					starredDashboards: profile.starredDashboards.add(id),
				}));
			}
		},

		cloneDashboard: async (sourceId, spec, { dashboard, options: { starred, publiclyAccessible } }) => {
			const { id } = await clone({
				idOrIdentifier: sourceId,
				diff: {
					...dashboard,
					spec,
					scope: publiclyAccessible ? Api.DashboardScope.PublicDashboard : Api.DashboardScope.PrivateDashboard,
				},
			});

			if (starred) {
				// do not wait for the optimistic profile update
				updateProfile((profile) => ({
					...profile,
					starredDashboards: profile.starredDashboards.add(id),
				}));
			}
		},

		editDashboard: (id, form, { dashboard, options: { starred, publiclyAccessible } }) =>
			tap((scope) => {
				// show the success edit notification even when nothing is modified
				if (!form.isDirty(scope)) {
					return onSuccessfulEdit(dashboard.name);
				}

				// edit only if the form is dirty
				if (form.fields.dashboard.isDirty(scope) || form.fields.options.fields.publiclyAccessible.isDirty(scope)) {
					edit({
						idOrIdentifier: id,
						diff: {
							...dashboard,
							scope: publiclyAccessible ? Api.DashboardScope.PublicDashboard : Api.DashboardScope.PrivateDashboard,
						},
					});

					// edit notification is enough
					shouldCallOnStar.current = false;
				} else {
					// show the (un)star notification if the dashboard is not edited
					shouldCallOnStar.current = true;
				}

				// star only if the form is dirty
				if (form.fields.options.fields.starred.isDirty(scope)) {
					updateProfile((profile) => ({
						...profile,
						starredDashboards: starred ? profile.starredDashboards.add(id) : profile.starredDashboards.delete(id),
					}));
				}
			}),
	};
};
